import { omit } from 'lodash';

import { Action } from '../actions';
import {
    CLOSE_MASS_ACTIONS_TURBOLANDING_BULK_REPLACEMENT_EDITOR,
    ON_ACCEPT_MASS_ACTIONS_TRACKING_PARAMETERS_BULK_REPLACEMENT_EDITOR,
    ON_ACCEPT_MASS_ACTIONS_TURBOLANDING_BULK_REPLACEMENT_EDITOR,
    ON_MASS_ACTIONS_TURBOLANDING_BULK_REPLACEMENT_EDITOR_RADIO_BUTTON_STATE_CHANGED,
    OPEN_MASS_ACTIONS_TURBOLANDING_BULK_REPLACEMENT_EDITOR
} from '../actions/banners-edit/banners-edit-mass-actions';
import {
    CLEAR_BANNERS,
    COPY_BANNER,
    CopyBanner,
    DELETE_BANNERS,
    RESET_BANNERS_EDIT,
    SAVE_BANNERS_SUCCESS,
    SaveBannersSuccess
} from '../actions/bannersEdit';
import {
    CLOSE_TURBOLANDINGS_CONSTRUCTOR,
    CloseTurbolandingsConstructor,
    GET_TURBOLANDING_INFO,
    GetTurbolandingInfo,
    OPEN_TURBOLANDINGS_CONSTRUCTOR,
    OpenTurbolandingsConstructor,
    RESET_TURBOLANDINGS,
    UPDATE_TURBOLANDING,
    UPDATE_TURBOLANDINGS,
    UpdateTurbolanding,
    UpdateTurbolandings
} from '../actions/turbolandings';
import {
    selectTurbolandingDataByTrackingParametersReplace,
    selectTurbolandingDataByTurbolandingReplace
} from '../selectors/bannerEdit/massActions/massActions';
import { EditedBannerTurbolanding } from '../typings/bannersEditState';
import { EditorPlaceId } from '../typings/common';
import { State } from '../typings/state';
import { TurbolandingsState } from '../typings/turbolandings';

export const TurbolandingsInitialState: TurbolandingsState = {};

export function turbolandings(
    state: TurbolandingsState = TurbolandingsInitialState,
    action: Action,
    fullState: State
): TurbolandingsState {
    switch (action.type) {
        case RESET_BANNERS_EDIT:
            return TurbolandingsInitialState;
        case UPDATE_TURBOLANDINGS:
            return updateTurbolandings(state, action);
        case RESET_TURBOLANDINGS:
            return resetTurbolandings(state, action.data);
        case OPEN_TURBOLANDINGS_CONSTRUCTOR:
            return openTurbolandingsConstructor(state, action);
        case CLOSE_TURBOLANDINGS_CONSTRUCTOR:
            return closeTurbolandingsConstructor(state, action);
        case GET_TURBOLANDING_INFO:
            return getTurbolandingInfo(state, action);
        case UPDATE_TURBOLANDING:
            return updateTurbolanding(state, action);
        case OPEN_MASS_ACTIONS_TURBOLANDING_BULK_REPLACEMENT_EDITOR:
        case CLOSE_MASS_ACTIONS_TURBOLANDING_BULK_REPLACEMENT_EDITOR:
        case ON_MASS_ACTIONS_TURBOLANDING_BULK_REPLACEMENT_EDITOR_RADIO_BUTTON_STATE_CHANGED:
            return removeTurbolandings(state, [action.massActionId]);
        case ON_ACCEPT_MASS_ACTIONS_TURBOLANDING_BULK_REPLACEMENT_EDITOR:
            return resetTurbolandings(state, {
                ...selectTurbolandingDataByTurbolandingReplace(
                    fullState,
                    action.massActionId
                ),
                [action.massActionId]: {
                    page: undefined,
                    expanded: false,
                    trackParams: ''
                }
            });
        case CLEAR_BANNERS:
            return deleteTurbolandings(
                state,
                Object.keys(action.bannersToClear)
            );
        case DELETE_BANNERS:
            return deleteTurbolandings(state, action.bannersIds);
        case ON_ACCEPT_MASS_ACTIONS_TRACKING_PARAMETERS_BULK_REPLACEMENT_EDITOR:
            return selectTurbolandingDataByTrackingParametersReplace(fullState);
        case SAVE_BANNERS_SUCCESS:
            return setSavedTurbolandings(state, action);
        case COPY_BANNER:
            return copyBannerTurboLandingInfo(state, action);
        default:
            return state;
    }
}

const setSavedTurbolandings = (
    state: TurbolandingsState,
    action: SaveBannersSuccess
): TurbolandingsState => {
    const { added } = action.result;
    const turboData = state;

    if (added.banners.length) {
        added.banners.forEach((addedBanner, index) => {
            if (!addedBanner) {
                return;
            }
            const oldId = added.ids[index];
            const newId = String(addedBanner.id);

            turboData[newId] = {
                ...turboData[oldId]
            };

            delete turboData[oldId];
        });

        state = turboData;
    }

    return state;
};

const deleteTurbolandings = (
    state: TurbolandingsState,
    ids: EditorPlaceId[]
): TurbolandingsState => {
    const newState = omit(state, ids);

    return newState;
};

const removeTurbolandings = (
    state: TurbolandingsState,
    ids: EditorPlaceId[]
): TurbolandingsState => {
    const result = ids.reduce((acc, id) => {
        return {
            [id]: {
                page: undefined,
                expanded: false,
                trackParams: ''
            }
        };
    }, {});

    return {
        ...state,
        ...result
    };
};

const resetTurbolandings = (
    state: TurbolandingsState,
    data: Record<EditorPlaceId, EditedBannerTurbolanding>
): TurbolandingsState => {
    return {
        ...state,
        ...data
    };
};

const updateTurbolandings = (
    state: TurbolandingsState,
    action: UpdateTurbolandings
): TurbolandingsState => {
    return action.data;
};

const openTurbolandingsConstructor = (
    state: TurbolandingsState,
    action: OpenTurbolandingsConstructor
): TurbolandingsState => {
    return {
        ...state,
        [action.id]: {
            ...state[action.id],
            templateId: action.payload && action.payload.templateId,
            enableTemplates: action.payload && action.payload.enableTemplates,
            disableTemplates: action.payload && action.payload.disableTemplates,
            isCanvasOpened: true
        }
    };
};

const closeTurbolandingsConstructor = (
    state: TurbolandingsState,
    action: CloseTurbolandingsConstructor
): TurbolandingsState => {
    return {
        ...state,
        [action.id]: {
            ...state[action.id],
            isCanvasOpened: false
        }
    };
};

const getTurbolandingInfo = (
    state: TurbolandingsState,
    action: GetTurbolandingInfo
): TurbolandingsState => {
    return {
        ...state,
        [action.id]: {
            ...state[action.id],
            isCanvasOpened: false,
            loading: true
        }
    };
};

const updateTurbolanding = (
    state: TurbolandingsState,
    action: UpdateTurbolanding
): TurbolandingsState => {
    return {
        ...state,
        [action.id]: {
            page: action.page,
            loading: false
        }
    };
};

const copyBannerTurboLandingInfo = (
    state: TurbolandingsState,
    action: CopyBanner
): TurbolandingsState => {
    const turboInfo = { ...state[action.bannerId] };

    if (!turboInfo) {
        return state;
    }

    return {
        ...state,
        [action.newBannerId]: turboInfo
    };
};
